import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const apiBase = `${process.env.REACT_APP_API_BASE_URL}/api/fee-proceedings-headers`;
const quarterNoMap = {
  Q1: 1,
  Q2: 2,
  Q3: 3,
  Q4: 4,
};
const dateText = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value).slice(0, 10)
    : d.toLocaleDateString("en-GB", { timeZone: "UTC" });
};
const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });

export default function FeeProceedingsHeaders() {
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const api = `${apiBase}/${clientId}/${collegeId}`;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [years, setYears] = useState([]);
  const [acyearId, setAcyearId] = useState("");
  const [quarter, setQuarter] = useState("Q1");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [releaseRow, setReleaseRow] = useState(null);
  const [releaseForm, setReleaseForm] = useState({
    bankname: "",
    branchname: "",
    releaseddate: "",
  });
  const [tabValue, setTabValue] = useState(0);

  // Student proceedings successfully uploaded during this screen session.
  const [uploadedStudentRows, setUploadedStudentRows] = useState([]);
  const [uploadRow, setUploadRow] = useState(null);
  const [studentCsvFile, setStudentCsvFile] = useState(null);
  const [studentCsvPreview, setStudentCsvPreview] = useState(null);
  const notify = (text, severity = "success") => setMessage({ text, severity });

  const loadHeaders = async () => {
    if (!acyearId || !quarter) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${api}/headers`, {
        params: { acyear_id: acyearId, quarterno: quarter },
      });
      setRows(Array.isArray(data) ? data : data.data || []);
    } catch (e) {
      notify(e.response?.data?.message || "Unable to load proceeding headers.", "error");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!clientId || !collegeId) {
      notify("Client or college information is missing from sessionStorage.user.", "error");
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${api}/academic-years`);
        const list = data.data || [];
        setYears(list);
        const active = list.find((x) => Number(x.is_active) === 1) || list[0];
        if (active) setAcyearId(String(active.acyear_id));
      } catch (e) {
        notify(e.response?.data?.message || "Unable to load academic years.", "error");
      }
    })();
  }, [clientId, collegeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (acyearId && quarter) loadHeaders();
  }, [acyearId, quarter]); // eslint-disable-line react-hooks/exhaustive-deps

  const previewColumns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "SNo",
        width: 70,
        sortable: false,
        filterable: false,
        valueGetter: (p) => p.api.getRowIndex(p.id) + 1,
      },
      { field: "_rowNumber", headerName: "CSV Row", width: 90 },
      { field: "proceedingno", headerName: "Proceeding No", minWidth: 100, flex: 1 },
      { field: "proceedingdate", headerName: "Proceeding Date", width: 140 },
      { field: "caste", headerName: "Caste", minWidth: 200, flex: 1 },
      { field: "bankdetails", headerName: "Bank Details", minWidth: 150, flex: 1 },
      { field: "billnumber", headerName: "Bill Number", width: 130 },
      { field: "billstatus", headerName: "Bill Status", width: 120 },
      { field: "releasecount", headerName: "Release Count", width: 120 },
      { field: "amount", headerName: "Amount", width: 130, valueFormatter: (p) => money(p.value) },
      {
        field: "_duplicate",
        headerName: "Import Status",
        width: 170,
        renderCell: (p) => (
          <Typography color={p.value ? "error.main" : "success.main"} fontWeight={700}>
            {p.value ? "Duplicate — skip" : "Ready to import"}
          </Typography>
        ),
      },
    ],
    []
  );
  const studentPreviewColumns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 70,
      },
      {
        field: "scholarAppNo",
        headerName: "Student ID",
        width: 130,
      },
      {
        field: "studentName",
        headerName: "Student Name",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "courseName",
        headerName: "Course",
        width: 140,
      },
      {
        field: "courseYear",
        headerName: "Course Year",
        width: 110,
      },
      {
        field: "status",
        headerName: "Payment Status",
        width: 150,
      },
      {
        field: "creditedDate",
        headerName: "Credited Date",
        width: 130,
      },
      {
        field: "releasedAmount",
        headerName: "Released Amount",
        width: 140,
        valueFormatter: (p) => money(p.value),
      },
      {
        field: "valid",
        headerName: "Validation",
        width: 120,
        renderCell: (p) => (
          <Typography
            fontWeight={700}
            color={
              p.row.ignored
                ? "warning.main"
                : p.row.duplicate
                ? "error.main"
                : p.value
                ? "success.main"
                : "error.main"
            }
          >
            {p.row.ignored
              ? "Ignored"
              : p.row.duplicate
              ? "Duplicate"
              : p.value
              ? "Valid"
              : "Invalid"}
          </Typography>
        ),
      },
      {
        field: "message",
        headerName: "Message",
        minWidth: 220,
        flex: 1,
      },
    ],
    []
  );
  const uploadedStudentColumns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 70,
        sortable: false,
        valueGetter: (p) => p.api.getRowIndex(p.id) + 1,
      },
      {
        field: "proceedingno",
        headerName: "Proceeding No",
        width: 160,
      },
      {
        field: "academic_year",
        headerName: "Academic Year",
        width: 130,
      },
      {
        field: "quarterno",
        headerName: "Quarter",
        width: 100,
      },
      {
        field: "scholarAppNo",
        headerName: "Student ID",
        width: 140,
      },
      {
        field: "studentName",
        headerName: "Student Name",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "courseName",
        headerName: "Course",
        width: 150,
      },
      {
        field: "courseYear",
        headerName: "Course Year",
        width: 120,
      },
      {
        field: "status",
        headerName: "Payment Status",
        width: 150,
      },
      {
        field: "creditedDate",
        headerName: "Credited Date",
        width: 130,
      },
      {
        field: "releasedAmount",
        headerName: "Released Amount",
        width: 150,
        valueFormatter: (p) => money(p.value),
      },
      {
        field: "uploaded_on",
        headerName: "Uploaded On",
        width: 160,
        valueFormatter: (p) => dateText(p.value),
      },
    ],
    []
  );
  const getUploadedStats = (row) => {
    const uploaded = uploadedStudentRows.filter(
      (student) => Number(student.proceedings_header_id) === Number(row.proceedings_header_id)
    );

    return {
      count: uploaded.length,
      amount: uploaded.reduce((total, student) => total + Number(student.releasedAmount || 0), 0),
    };
  };

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 70,
        sortable: false,
        filterable: false,
        valueGetter: (p) => p.api.getRowIndex(p.id) + 1,
      },
      {
        field: "proceedings_header_id",
        headerName: " ID",
        width: 50,
      },
      {
        field: "academic_year",
        headerName: "Academic Year",
        width: 125,
      },
      {
        field: "proceedingno",
        headerName: "Proceeding No",
        minWidth: 100,
        flex: 1,
      },
      {
        field: "proceedingdate",
        headerName: "Proceeding Date",
        width: 135,
        valueFormatter: (p) => dateText(p.value),
      },
      {
        field: "caste",
        headerName: "Caste",
        width: 180,
      },

      // Quarter + Release Count + Amount combined
      {
        field: "proceeding_summary",
        headerName: "Quarter / Count / Amount",
        width: 210,
        sortable: false,
        renderCell: (p) => (
          <Box>
            <Typography variant="body2">
              <b>Quarter:</b> {p.row.quarterno || "-"}
            </Typography>
            <Typography variant="body2">
              <b>Count:</b> {p.row.releasecount ?? 0}
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              <b>Amount:</b> {money(p.row.amount)}
            </Typography>
          </Box>
        ),
      },

      // Bank + Branch + Released Date combined
      {
        field: "release_details",
        headerName: "Release Details",
        width: 220,
        sortable: false,
        renderCell: (p) => (
          <Box>
            <Typography variant="body2">
              <b>Bank:</b> {p.row.bankname || "-"}
            </Typography>
            <Typography variant="body2">
              <b>Branch:</b> {p.row.branchname || "-"}
            </Typography>
            <Typography variant="body2">
              <b>Date:</b> {dateText(p.row.releaseddate) || "-"}
            </Typography>
          </Box>
        ),
      },

      // Uploaded student count
      {
        field: "uploaded_count",
        headerName: "Uploaded Count",
        width: 130,
        sortable: false,
        renderCell: (p) => {
          const uploaded = Number(p.row.uploaded_count ?? 0);
          const expected = Number(p.row.releasecount ?? 0);

          return (
            <Typography
              fontWeight={500}
              variant="body2"
              color={uploaded >= expected && expected > 0 ? "success.main" : "text.primary"}
            >
              {uploaded} / {expected}
            </Typography>
          );
        },
      },

      // Uploaded student amount
      {
        field: "uploaded_total_amount",
        headerName: "Uploaded Amount",
        width: 155,
        sortable: false,
        renderCell: (p) => (
          <Typography fontWeight={500} variant="body2">
            {money(Number(p.row.uploaded_total_amount ?? 0))}
          </Typography>
        ),
      },

      {
        field: "approvestatus",
        headerName: "Approval",
        width: 120,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const isApproved = Number(params.value) === 1;

          return (
            <Box
              sx={{
                color: isApproved ? "#2e7d32" : "#ed6c02",
                backgroundColor: isApproved ? "#e8f5e9" : "#fff3e0",
                fontWeight: 600,
                fontSize: "12px",
                px: 1.2,
                py: 0.5,
                borderRadius: "12px",
                textAlign: "center",
                minWidth: 75,
              }}
            >
              {isApproved ? "Approved" : "Pending"}
            </Box>
          );
        },
      },

      {
        field: "actions",
        headerName: "Actions",
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: (p) => {
          const isApproved = Number(p.row.approvestatus) === 1;
          const uploadedCount = Number(p.row.uploaded_count ?? 0);
          const expected = Number(p.row.releasecount ?? 0);
          const isUploaded = uploadedCount > 0;

          if (!isApproved) {
            return (
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => {
                  setReleaseRow(p.row);
                  setReleaseForm({
                    bankname: p.row.bankname || "",
                    branchname: p.row.branchname || "",
                    releaseddate: p.row.releaseddate ? String(p.row.releaseddate).slice(0, 10) : "",
                  });
                }}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#FFFFFF",
                  "&:hover": { backgroundColor: "#1565c0", color: "#FFFFFF" },
                }}
              >
                Update Bank
              </Button>
            );
          }

          return (
            <>
              <Button
                size="small"
                variant="contained"
                startIcon={isUploaded ? undefined : <UploadFileIcon />}
                disabled={busy || isUploaded}
                onClick={() => {
                  setUploadRow(p.row);
                  setStudentCsvFile(null);
                  setStudentCsvPreview(null);
                }}
                sx={{
                  backgroundColor: isUploaded ? "#2e7d32" : "#8519d2c2",
                  color: "#fff",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: isUploaded ? "#2e7d32" : "#7915c0",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                  },
                }}
              >
                {isUploaded ? "Uploaded" : "Upload Proceedings"}
              </Button>
              {isUploaded && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => downloadProceedingsPdf(p.row)}
                >
                  PDF
                </Button>
              )}
            </>
          );
        },
      },
    ],
    [uploadedStudentRows, busy]
  );

  const validateProceedingAmount = (selectedProceedingAmount, studentProceedingAmount) => {
    const selectedAmount = Number(selectedProceedingAmount);
    const studentAmount = Number(studentProceedingAmount);

    if (!Number.isFinite(selectedAmount) || !Number.isFinite(studentAmount)) {
      return false;
    }

    return Math.abs(selectedAmount - studentAmount) < 0.01;
  };
  const validateProceedingsFileName = (file, proceedingNumber) => {
    if (!file || !proceedingNumber) {
      return false;
    }

    const fileNameWithoutExtension = file.name.trim().replace(/\.[^/.]+$/, "");

    return fileNameWithoutExtension.trim() === String(proceedingNumber).trim();
  };
  const handleStudentCsvFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const proceedingNumber = uploadRow?.proceedingno;

    if (!validateProceedingsFileName(file, proceedingNumber)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Filename",
        text: `Uploaded filename must be "${proceedingNumber}" without the file extension.`,
        confirmButtonColor: "#1976d2",
        target: document.body,
        didOpen: () => {
          const popup = Swal.getPopup();

          if (popup) {
            popup.style.zIndex = "20000";
          }

          const container = Swal.getContainer();

          if (container) {
            container.style.zIndex = "20000";
          }
        },
      });

      setStudentCsvFile(null);
      setStudentCsvPreview(null);
      event.target.value = "";
      return;
    }

    setStudentCsvFile(file);
    setStudentCsvPreview(null);
  };
  const doPreview = async () => {
    if (!acyearId || !quarter || !file)
      return notify("Select academic year, quarter and CSV file.", "warning");
    const form = new FormData();
    form.append("excel1", file);
    form.append("acyear_id", acyearId);
    form.append("quarterno", quarter);
    setBusy(true);
    try {
      const { data } = await axios.post(`${api}/upload/preview`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data.data);
    } catch (e) {
      notify(e.response?.data?.message || "CSV preview failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const previewStudentProceedings = async () => {
    if (!uploadRow || !studentCsvFile) {
      notify("Select a student CSV file.", "warning");
      return;
    }

    const form = new FormData();
    form.append("excel1", studentCsvFile);

    setBusy(true);

    try {
      const { data } = await axios.post(
        `${api}/headers/${uploadRow.proceedings_header_id}/student-upload/preview`,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Get the preview response
      const preview = data.data;

      // Compare selected proceeding amount with the Totals amount
      // extracted from the last column of the Totals row.
      const selectedAmount = Number(uploadRow.amount);
      const studentAmount = Number(preview?.totalAmount);

      if (
        !Number.isFinite(selectedAmount) ||
        !Number.isFinite(studentAmount) ||
        Math.abs(selectedAmount - studentAmount) >= 0.01
      ) {
        setStudentCsvPreview(null);

        Swal.fire({
          icon: "error",
          title: "Amount Mismatch",
          text: `Selected Proceeding Amount: ${
            Number.isFinite(selectedAmount) ? `₹${selectedAmount.toFixed(2)}` : "Invalid"
          }. Student Proceedings Total: ${
            Number.isFinite(studentAmount) ? `₹${studentAmount.toFixed(2)}` : "Invalid"
          }. Both amounts must be equal.`,
          confirmButtonColor: "#1976d2",
          didOpen: () => {
            const container = Swal.getContainer();
            if (container) {
              container.style.zIndex = "20000";
            }
          },
        });

        return;
      }

      // Show preview only when the amounts match.
      setStudentCsvPreview(preview);
      notify("Student proceedings preview loaded.", "success");
    } catch (error) {
      setStudentCsvPreview(null);
      notify(error.response?.data?.message || "Student proceedings preview failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const saveStudentProceedings = async () => {
    if (!uploadRow || !studentCsvPreview?.rows?.length) {
      notify("Please preview the student CSV first.", "warning");
      return;
    }

    if (Number(studentCsvPreview.invalidRows || 0) > 0) {
      notify("Invalid student rows are present. Please correct the CSV.", "warning");
      return;
    }

    setBusy(true);

    try {
      const { data } = await axios.post(
        `${api}/headers/${uploadRow.proceedings_header_id}/student-upload/save`,
        {
          proceedings_header_id: Number(uploadRow.proceedings_header_id),
          quarterno: Number(
            quarterNoMap[
              String(uploadRow.quarterno || quarter)
                .trim()
                .toUpperCase()
            ]
          ),
          rows: studentCsvPreview.rows,
          created_by: user?.user_email || user?.email,
        }
      );

      // Open the generated PDF if the backend returned its URL.
      if (data?.pdf_url) {
        const pdfUrl = new URL(data.pdf_url, api).href;

        window.open(pdfUrl, "_blank", "noopener,noreferrer");
      } else if (data?.pdf_error) {
        notify(`Receipts saved, but PDF generation failed: ${data.pdf_error}`, "warning");
      }

      // Build the rows for the second DataGrid.
      const uploadedRows = studentCsvPreview.rows
        .filter((row) => !row.ignored && !row.duplicate && row.valid !== false)
        .map((row, index) => ({
          ...row,

          // Unique DataGrid row ID
          id: row._rowNumber || row.sno || `${uploadRow.proceedings_header_id}-${index}`,

          proceedings_header_id: uploadRow.proceedings_header_id,
          proceedingno: uploadRow.proceedingno,
          academic_year: uploadRow.academic_year,
          quarterno: uploadRow.quarterno,
          uploaded_on: new Date().toISOString(),
        }));

      setUploadedStudentRows((previous) => [...uploadedRows, ...previous]);

      notify(data.message || "Student proceedings uploaded successfully.");

      // Clear upload selection and preview.
      setUploadRow(null);
      setStudentCsvFile(null);
      setStudentCsvPreview(null);

      // Automatically move to second tab.
      setTabValue(1);

      await loadHeaders();
    } catch (error) {
      notify(error.response?.data?.message || "Unable to generate receipts.", "error");
    } finally {
      setBusy(false);
    }
  };
  const downloadProceedingsPdf = async (row) => {
    try {
      const response = await axios.get(
        `${api}/headers/${row.proceedings_header_id}/student-upload/pdf`,
        {
          responseType: "blob",
        }
      );

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `Government-Proceedings-${row.proceedingno || row.proceedings_header_id}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      notify("Unable to download proceedings PDF.", "error");
    }
  };

  const downloadBulkProceedingsPdf = async () => {
    const uploadedHeaders = rows.filter((row) => Number(row.uploaded_count ?? 0) > 0);

    if (!uploadedHeaders.length) {
      notify("No uploaded student proceedings found.", "warning");
      return;
    }

    const confirmed = await Swal.fire({
      icon: "question",
      title: "Generate Bulk PDF?",
      text: `Generate one PDF for ${uploadedHeaders.length} uploaded proceedings in the selected academic year and quarter?`,
      showCancelButton: true,
      confirmButtonText: "Generate PDF",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1976d2",
      cancelButtonColor: "#d32f2f",
    });

    if (!confirmed.isConfirmed) return;

    setBusy(true);

    try {
      const response = await axios.post(
        `${api}/bulk-student-upload/pdf`,
        {
          acyear_id: Number(acyearId),
          quarterno: quarterNoMap[quarter],
          proceedings_header_ids: uploadedHeaders.map((row) => Number(row.proceedings_header_id)),
        },
        { responseType: "blob" }
      );

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `Government-Proceedings-${quarter}-${acyearId}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
      notify("Bulk proceedings PDF downloaded successfully.");
    } catch (error) {
      // Axios may receive a JSON error as a Blob because responseType is "blob".
      let errorMessage = "Unable to generate bulk proceedings PDF.";

      if (error.response?.data instanceof Blob) {
        try {
          const errorData = JSON.parse(await error.response.data.text());
          errorMessage = errorData.message || errorMessage;
        } catch {
          // Keep the default error message.
        }
      } else {
        errorMessage = error.response?.data?.message || errorMessage;
      }

      notify(errorMessage, "error");
    } finally {
      setBusy(false);
    }
  };

  const savePreview = async () => {
    if (!preview?.rows?.length) return;
    const accepted = preview.rows.filter((r) => !r._duplicate);
    if (!accepted.length) return notify("All rows are duplicates. Nothing to import.", "warning");
    setBusy(true);
    try {
      const { data } = await axios.post(`${api}/upload/save`, {
        acyear_id: acyearId,
        quarterno: quarter,
        rows: preview.rows,
        createdby: user?.user_email || user?.email,
      });
      notify(
        `${data.insertedCount} header(s) uploaded; ${data.skippedDuplicateCount} duplicate row(s) skipped.`
      );
      setPreview(null);
      setFile(null);
      await loadHeaders();
    } catch (e) {
      notify(e.response?.data?.message || "CSV import failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const saveRelease = async () => {
    if (!releaseRow) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${api}/headers/${releaseRow.proceedings_header_id}/release`,
        releaseForm
      );
      notify(data.data?.message || "Release details updated.");
      setReleaseRow(null);
      await loadHeaders();
    } catch (e) {
      notify(e.response?.data?.message || "Unable to update release details.", "error");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const fields = [
      "proceedings_header_id",
      "academic_year",
      "proceedingno",
      "proceedingdate",
      "caste",
      "quarterno",
      "releasecount",
      "amount",
      "bankname",
      "branchname",
      "releaseddate",
      "approvestatus",
      "billnumber",
      "billstatus",
      "bankdetails",
    ];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv =
      "\uFEFF" +
      [fields.join(","), ...rows.map((r) => fields.map((f) => escape(r[f])).join(","))].join(
        "\r\n"
      );
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "proceedingsheaders.csv";
    link.click();
    URL.revokeObjectURL(url);
  };
  const handlePrint = () => {
    if (!rows || rows.length === 0) {
      notify("No proceedings headers available to print.", "warning");
      return;
    }

    const printWindow = window.open("", "_blank", "width=1400,height=800");

    if (!printWindow) {
      notify("Please allow pop-ups to print the headers.", "error");
      return;
    }

    const formatDate = (value) => {
      if (!value) return "-";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";

      return date.toLocaleDateString("en-GB");
    };

    const formatAmount = (value) =>
      Number(value || 0).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
      });

    const escapeHtml = (value) =>
      String(value ?? "-").replace(
        /[&<>"']/g,
        (char) =>
          ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
          }[char])
      );

    const tableRows = rows
      .map(
        (row) => `
        <tr>
          <td>${escapeHtml(row.proceedings_header_id)}</td>
          <td>${escapeHtml(row.academic_year)}</td>
          <td>${escapeHtml(row.proceedingno)}</td>
          <td>${formatDate(row.proceedingdate)}</td>
          <td>${escapeHtml(row.caste)}</td>
          <td>${escapeHtml(row.quarterno)}</td>
          <td class="number">${escapeHtml(row.releasecount)}</td>
          <td class="number">${formatAmount(row.amount)}</td>
          <td>${escapeHtml(row.bankname)}</td>
          <td>${escapeHtml(row.branchname)}</td>
          <td>${formatDate(row.releaseddate)}</td>
          <td>${Number(row.approvestatus) === 1 ? "Approved" : "Pending"}</td>
          <td>${escapeHtml(row.billnumber)}</td>
          <td>${escapeHtml(row.billstatus)}</td>
          <td class="action">Update Release</td>
        </tr>
      `
      )
      .join("");

    const academicYear = rows[0]?.academic_year || "-";

    const quarter = rows[0]?.quarterno || "-";

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>GET PROCEEDINGS HEADERS</title>
        <style>
          @page {
            size: landscape;
            margin: 8mm;
          }

          body {
            font-family: Arial, sans-serif;
            font-size: 9px;
            color: #222;
          }

          h2 {
            text-align: center;
            margin: 0 0 8px;
            font-size: 17px;
          }

          .subtitle {
            text-align: center;
            margin-bottom: 15px;
            font-size: 11px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: auto;
          }

          th, td {
            border: 1px solid #777;
            padding: 5px 4px;
            text-align: left;
            overflow-wrap: anywhere;
          }

          th {
            background: #e8eef7;
            font-weight: bold;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          td.number {
            text-align: right;
            white-space: nowrap;
          }

          td.action {
            white-space: nowrap;
          }

          tr {
            page-break-inside: avoid;
          }
        </style>
      </head>
      <body>
        <h2>GET PROCEEDINGS HEADERS</h2>

        <div class="subtitle">
          Academic Year: ${escapeHtml(academicYear)}
          &nbsp; | &nbsp;
          Quarter: ${escapeHtml(quarter)}
        </div>

        <table>
          <thead>
            <tr>
              <th>Header ID</th>
              <th>Academic Year</th>
              <th>Proceeding No</th>
              <th>Proceeding Date</th>
              <th>Caste</th>
              <th>Quarter</th>
              <th>Release Count</th>
              <th>Amount</th>
              <th>Bank</th>
              <th>Branch</th>
              <th>Released Date</th>
              <th>Approval</th>
              <th>Bill Number</th>
              <th>Bill Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `);

    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };
  return (
    <Box p={{ xs: 1, sm: 2 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" fontWeight={700} mb={2}>
          HEADERS OF PROCEEDINGS
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Academic Year"
              value={acyearId}
              onChange={(e) => setAcyearId(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
            >
              <option value="">Select Academic Year</option>
              {years.map((y) => (
                <option key={y.acyear_id} value={String(y.acyear_id)}>
                  {y.academic_year}
                  {Number(y.is_active) === 1 ? " (Active)" : ""}
                </option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={3} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Quarter"
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
            >
              <option value="">Select Quarter</option>
              {QUARTERS.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={loadHeaders}
              disabled={busy || !acyearId}
              sx={{
                backgroundColor: "#1976d2",
                color: "#ffffff",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#1565c0",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#90caf9",
                  color: "#ffffff",
                },
              }}
            >
              Get Headers
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>
          UPLOAD PROCEEDING HEADERS CSV
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#ffffff",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#90caf9",
                color: "#ffffff",
              },
            }}
          >
            Choose CSV
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setPreview(null);
              }}
            />
          </Button>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {file?.name || "No file selected"}
          </Typography>
          <Button
            variant="contained"
            onClick={doPreview}
            disabled={busy || !file || !acyearId}
            startIcon={<SearchIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#ffffff",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#90caf9",
                color: "#ffffff",
              },
            }}
          >
            Preview CSV
          </Button>
        </Stack>
        <Typography variant="caption" display="block" mt={1}>
          CSV columns: SNo, ProceedingNo, ProceedingDate, Corporation (Caste), BankDetails,
          BillNumber, BillStatus, TotalNoofStudents(Count), Amount. The second row is skipped to
          match the existing PHP import.
        </Typography>
      </Paper>

      {busy && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress />
        </Box>
      )}

      {preview && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} mb={1}>
            CSV Preview
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" mb={2}>
            <Typography>Total: {preview.totalRows}</Typography>
            <Typography color="success.main">Ready: {preview.validRows}</Typography>
            <Typography color="error.main">Duplicates: {preview.duplicateRows}</Typography>
          </Stack>
          <Box sx={{ height: 380, width: "100%" }}>
            <DataGrid
              rows={preview.rows}
              columns={previewColumns}
              getRowId={(r) => r._rowNumber}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
          <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
            <Button color="error" variant="outlined" onClick={() => setPreview(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={savePreview}
              disabled={busy || preview.validRows === 0}
              sx={{
                backgroundColor: "#1976d2",
                color: "#ffffff",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#1565c0",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#90caf9",
                  color: "#ffffff",
                },
              }}
            >
              Import Valid Rows
            </Button>
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1}
          mb={2}
        >
          <Typography variant="h6" fontWeight={700}>
            GET PROCEEDINGS HEADERS
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={downloadBulkProceedingsPdf}
              disabled={busy || !rows.some((row) => Number(row.uploaded_count ?? 0) > 0)}
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#b71c1c" },
              }}
            >
              Bulk PDF
            </Button>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportCsv}
              disabled={!rows.length}
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#1565c0" },
              }}
            >
              Export CSV
            </Button>

            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={!rows.length}
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#1565c0" },
              }}
            >
              Print
            </Button>
          </Stack>
        </Stack>
        <Box sx={{ height: 620, width: "100%" }} id="proceedings-headers-print">
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.proceedings_header_id}
            rowHeight={75}
            pagination
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            loading={busy}
            getRowClassName={(params) => {
              const stats = getUploadedStats(params.row);
              const expected = Number(params.row.releasecount || 0);
              const uploaded = Number(params.row.uploadcount || 0);
              const isUploaded = expected > 0 && uploaded >= expected;

              return expected > 0 && stats.count >= expected ? "proceeding-upload-complete" : "";
            }}
            sx={{
              "& .MuiDataGrid-row": {
                minHeight: "75px !important",
              },
              "& .MuiDataGrid-cell": {
                whiteSpace: "normal",
                lineHeight: 1.4,
                paddingTop: "8px",
                paddingBottom: "8px",
              },
              "& .proceeding-upload-complete": {
                backgroundColor: "#e8f5e9",
              },
              "& .proceeding-upload-complete:hover": {
                backgroundColor: "#c8e6c9",
              },
              "& .proceeding-upload-complete.Mui-selected": {
                backgroundColor: "#c8e6c9",
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={Boolean(releaseRow)}
        onClose={() => setReleaseRow(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Update Release Details</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            Proceeding No: <b>{releaseRow?.proceedingno}</b>
          </Typography>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Bank Name"
              value={releaseForm.bankname}
              onChange={(e) => setReleaseForm({ ...releaseForm, bankname: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Bank Branch Name"
              value={releaseForm.branchname}
              onChange={(e) => setReleaseForm({ ...releaseForm, branchname: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Released Date"
              type="date"
              value={releaseForm.releaseddate}
              onChange={(e) => setReleaseForm({ ...releaseForm, releaseddate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => setReleaseRow(null)}
            sx={{
              backgroundColor: "#ef3f17b2",
              color: "#ffffff",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#e71b39",
              },
              "&.Mui-disabled": {
                backgroundColor: "#90caf9",
                color: "#ffffff",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveRelease}
            disabled={busy}
            sx={{
              backgroundColor: "#1976d2",
              color: "#ffffff",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#90caf9",
                color: "#ffffff",
              },
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(uploadRow)}
        onClose={() => {
          if (!busy) {
            setUploadRow(null);
            setStudentCsvFile(null);
            setStudentCsvPreview(null);
          }
        }}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Upload Student Proceedings</DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <b>Proceeding No:</b> {uploadRow?.proceedingno} {"  "}
            <b>Academic Year:</b> {uploadRow?.academic_year}
            <br />
            <b>Quarter:</b> {uploadRow?.quarterno} {"  "}
            <b>Approved Amount:</b> {money(uploadRow?.amount)}
          </Alert>

          <Stack spacing={2}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{
                alignSelf: "flex-start",
                color: "#1976d2",
                fontWeight: "bold",
              }}
            >
              Choose Student CSV
              <input
                hidden
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleStudentCsvFileChange}
              />
            </Button>

            <Typography variant="body2">
              {studentCsvFile?.name || "No student CSV selected"}
            </Typography>

            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              disabled={busy || !studentCsvFile}
              onClick={previewStudentProceedings}
              sx={{
                alignSelf: "flex-start",
                backgroundColor: "#1976d2",
                fontWeight: "bold",
                color: "#ffffff",
              }}
            >
              Preview CSV
            </Button>

            {studentCsvPreview && (
              <Box mt={2}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Student CSV preview loaded. Review the rows before generating receipts.
                </Alert>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
                  <Typography>
                    Total Rows: <b>{studentCsvPreview.totalRows}</b>
                  </Typography>

                  <Typography color="success.main">
                    Valid Rows: <b>{studentCsvPreview.validRows}</b>
                  </Typography>

                  <Typography color="error.main">
                    Duplicate Rows: <b>{studentCsvPreview.duplicateRows}</b>
                  </Typography>

                  <Typography color="warning.main">
                    Ignored Rows: <b>{studentCsvPreview.ignoredRows}</b>
                  </Typography>

                  <Typography color="error.main">
                    Invalid Rows: <b>{studentCsvPreview.invalidRows}</b>
                  </Typography>
                </Stack>

                <Box sx={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={studentCsvPreview.rows || []}
                    columns={studentPreviewColumns}
                    getRowId={(row) => row._rowNumber || row.sno}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectionOnClick
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setUploadRow(null);
              setStudentCsvFile(null);
              setStudentCsvPreview(null);
            }}
            disabled={busy}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={saveStudentProceedings}
            disabled={busy || !studentCsvPreview || Number(studentCsvPreview.invalidRows || 0) > 0}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#bdbdbd",
                color: "#fff",
              },
            }}
          >
            {busy ? "Processing..." : "Generate Receipts"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setMessage(null)}
      >
        {message && (
          <Alert severity={message.severity} variant="filled" onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}
